/*
================================================================================
File: src/pages/InvestorLeads/InvestorPages.js
Description: Displays, creates, and manages investor leads with full modal functionality.
             (Fixed: Uses Adaptive Hook and adds Multi-Select Bulk Delete)
================================================================================
*/
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAdaptiveMMKV } from "../../hooks/useAdaptiveMMKV";
import Select from "react-tailwindcss-select";
import instance from "../../api/axios";
import { storage } from "../../App";
import PageContent from "../../components/common/PageContent";
import Modal from "../../components/common/Modal";
import { formatMobileNumber } from "../../utils/formatters";
import {
  propOption,
  attributeOptions,
  countiesOptions,
  financeOptions,
  boolOptions,
} from "../../constants/options";
import { token } from "../../constants/options";

// --- Create Investor Modal ---
const CreateInvestorInfoModal = ({
  isOpen,
  onClose,
  user,
  onCreated,
  setError,
}) => {
  const initialFormState = {
    fullName: "",
    action: "investor",
    phone: "",
    website: "",
    email: "",
    minInvestment: "0",
    maxInvestment: "0",
    propertyTypes: [],
    attributes: [],
    investmentCounties: [],
    financeTypes: [],
    wantsToLend: { value: "0", label: "No" },
    notes: "",
    fileName: "",
    logo: null,
  };
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData((prev) => ({ ...prev, fileName: file.name, logo: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const dataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "logo") {
        if (formData.logo) dataToSend.append("logo", formData.logo);
        dataToSend.append("imageName", formData.fileName);
      } else if (Array.isArray(formData[key])) {
        dataToSend.append(
          key,
          formData[key].map((opt) => opt.value).join(", "),
        );
      } else if (typeof formData[key] === "object" && formData[key] !== null) {
        dataToSend.append(key, formData[key].value);
      } else {
        dataToSend.append(key, formData[key]);
      }
    });
    dataToSend.append("owner", user.app_id);
    dataToSend.append(
      "token",
      token(user.app_id, user.app_email, user.app_api_key),
    );

    try {
      await instance.post("/addInvestor.php", dataToSend);
      setError(null);
    } catch (e) {
      if (e.response) {
        setError(
          "Error adding investor: " + e.response?.data?.message || e.message,
        );
        console.error(
          "Error adding investor: ",
          e.response?.data?.message || e.message,
        );
      } else if (e.request) {
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      onCreated(); // Callback to refresh the list
      setLoading(false);
      onClose();
    }
  };

  const footer = (
    <button
      onClick={handleSubmit}
      disabled={loading}
      className="px-5 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      {loading ? "Creating..." : "Create Investor"}
    </button>
  );

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title="Create Investor Card"
      footer={footer}
      maxWidth="max-w-4xl"
      icon={
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      }
      iconColor="green"
    >
      <form className="space-y-6">
        {/* Identity Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              Identity
            </h3>
          </div>
          <div className="space-y-5">
            <div className="flex flex-col items-center">
              {formData.logo ? (
                <>
                  <img
                    src={URL.createObjectURL(formData.logo)}
                    alt="Preview"
                    className="h-32 w-32 rounded-full object-cover border-4 border-indigo-200 dark:border-indigo-900 mb-4"
                  />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {formData.fileName}
                  </p>
                </>
              ) : (
                <div className="h-32 w-32 rounded-full bg-gray-200 dark:bg-gray-700 border-4 border-indigo-200 dark:border-indigo-900 mb-4 flex items-center justify-center">
                  <svg
                    className="h-16 w-16 text-gray-400 dark:text-gray-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Upload Image
              </label>
              <input
                type="file"
                name="logo"
                onChange={handleFileChange}
                className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100 dark:file:bg-indigo-900/30 dark:file:text-indigo-400"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Website
              </label>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
              />
            </div>
          </div>
        </div>

        {/* Investment Range Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.16 2.75a.75.75 0 00-1.08.6v5.69H1.75a.75.75 0 00-.75.75v2.5c0 .414.336.75.75.75h5.37v5.69c0 .464.576.734 1.08.6a60.6 60.6 0 018.62-17a.75.75 0 00-.62-1.11H8.16z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              Investment Range
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Min. Investment
              </label>
              <input
                type="number"
                name="minInvestment"
                value={formData.minInvestment}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max. Investment
              </label>
              <input
                type="number"
                name="maxInvestment"
                value={formData.maxInvestment}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Wants to Lend Money
              </label>
              <Select
                value={formData.wantsToLend}
                onChange={(v) => handleSelectChange("wantsToLend", v)}
                options={boolOptions}
                classNames={{
                  menuButton: ({ isDisabled }) =>
                    `flex text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-emerald-500 focus:ring focus:ring-emerald-500/20 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                      isDisabled ? "bg-gray-200" : ""
                    }`,
                  menu: "absolute z-10 w-full bg-white shadow-lg border border-gray-300 rounded-md py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200",
                  listItem: ({ isSelected }) =>
                    `block transition duration-200 px-3 py-2 cursor-pointer select-none truncate rounded ${
                      isSelected
                        ? "text-white bg-emerald-500"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
                    }`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Investment Preferences Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M6.707 2.293a1 1 0 00-1.414 1.414l9 9a1 1 0 001.414-1.414l-9-9zM4 10a2 2 0 11-4 0 2 2 0 014 0zm12 0a2 2 0 11-4 0 2 2 0 014 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              Investment Preferences
            </h3>
          </div>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Property Types
              </label>
              <Select
                isMultiple
                isSearchable
                value={formData.propertyTypes}
                onChange={(v) => handleSelectChange("propertyTypes", v)}
                options={propOption}
                classNames={{
                  menuButton: ({ isDisabled }) =>
                    `flex text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-teal-500 focus:ring focus:ring-teal-500/20 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                      isDisabled ? "bg-gray-200" : ""
                    }`,
                  menu: "absolute z-10 w-full bg-white shadow-lg border border-gray-300 rounded-md py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200",
                  listItem: ({ isSelected }) =>
                    `block transition duration-200 px-3 py-2 cursor-pointer select-none truncate rounded ${
                      isSelected
                        ? "text-white bg-teal-500"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
                    }`,
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Attributes
              </label>
              <Select
                isMultiple
                isSearchable
                value={formData.attributes}
                onChange={(v) => handleSelectChange("attributes", v)}
                options={attributeOptions}
                classNames={{
                  menuButton: ({ isDisabled }) =>
                    `flex text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-teal-500 focus:ring focus:ring-teal-500/20 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                      isDisabled ? "bg-gray-200" : ""
                    }`,
                  menu: "absolute z-10 w-full bg-white shadow-lg border border-gray-300 rounded-md py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200",
                  listItem: ({ isSelected }) =>
                    `block transition duration-200 px-3 py-2 cursor-pointer select-none truncate rounded ${
                      isSelected
                        ? "text-white bg-teal-500"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
                    }`,
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Investment Counties
              </label>
              <Select
                isMultiple
                isSearchable
                value={formData.investmentCounties}
                onChange={(v) => handleSelectChange("investmentCounties", v)}
                options={countiesOptions}
                classNames={{
                  menuButton: ({ isDisabled }) =>
                    `flex text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-teal-500 focus:ring focus:ring-teal-500/20 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                      isDisabled ? "bg-gray-200" : ""
                    }`,
                  menu: "absolute z-10 w-full bg-white shadow-lg border border-gray-300 rounded-md py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200",
                  listItem: ({ isSelected }) =>
                    `block transition duration-200 px-3 py-2 cursor-pointer select-none truncate rounded ${
                      isSelected
                        ? "text-white bg-teal-500"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
                    }`,
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Finance Types
              </label>
              <Select
                isMultiple
                isSearchable
                value={formData.financeTypes}
                onChange={(v) => handleSelectChange("financeTypes", v)}
                options={financeOptions}
                classNames={{
                  menuButton: ({ isDisabled }) =>
                    `flex text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-teal-500 focus:ring focus:ring-teal-500/20 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                      isDisabled ? "bg-gray-200" : ""
                    }`,
                  menu: "absolute z-10 w-full bg-white shadow-lg border border-gray-300 rounded-md py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200",
                  listItem: ({ isSelected }) =>
                    `block transition duration-200 px-3 py-2 cursor-pointer select-none truncate rounded ${
                      isSelected
                        ? "text-white bg-teal-500"
                        : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
                    }`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Personal Notes Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2H4a1 1 0 110-2V4z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
              Additional Notes
            </h3>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Personal Notes about Investor
            </label>
            <textarea
              name="notes"
              rows="4"
              value={formData.notes}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

// --- Investor Detail Modal ---
const InvestorDetailModal = ({ isOpen, onClose, onSave, investor }) => {
  const [activeTab, setActiveTab] = useState("home");
  const [isEditing, setIsEditing] = useState(false);
  const [editableInvestor, setEditableInvestor] = useState({ ...investor });

  useEffect(() => {
    setEditableInvestor({ ...investor });
  }, [investor]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableInvestor((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setEditableInvestor((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const previewUrl = URL.createObjectURL(file);
      setEditableInvestor((prev) => ({
        ...prev,
        imageUrl: previewUrl,
        logo: file,
        imageName: file.name,
      }));
    }
  };

  const handleSave = () => {
    onSave(editableInvestor);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditableInvestor({ ...investor });
    setIsEditing(false);
  };

  const footer = isEditing ? (
    <div className="flex gap-3">
      <button
        onClick={handleCancel}
        className="px-5 py-2 text-sm font-semibold bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Save Changes
      </button>
    </div>
  ) : (
    <button
      onClick={() => setIsEditing(true)}
      className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      Edit Investor
    </button>
  );

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title="Investor Details Card"
      footer={footer}
      maxWidth="max-w-3xl"
      icon={
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      }
      iconColor="blue"
    >
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab("home")}
            className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${
              activeTab === "home"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Investor Data
          </button>
          <button
            onClick={() => setActiveTab("attributes")}
            className={`px-6 py-3 font-semibold text-sm border-b-2 transition-colors ${
              activeTab === "attributes"
                ? "border-blue-500 text-blue-600 dark:text-blue-400"
                : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Investor Attributes
          </button>
        </nav>
      </div>
      {activeTab === "home" && (
        <HomeTabContent
          isEditing={isEditing}
          editableInvestor={editableInvestor}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          handleImageChange={handleImageChange}
        />
      )}
      {activeTab === "attributes" && (
        <AttributesTabContent
          isEditing={isEditing}
          editableInvestor={editableInvestor}
          handleSelectChange={handleSelectChange}
        />
      )}
    </Modal>
  );
};

const HomeTabContent = ({
  isEditing,
  editableInvestor,
  handleInputChange,
  handleSelectChange,
  handleImageChange,
}) => (
  <div className="p-8 space-y-6">
    {/* Profile Section */}
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
        <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
          Investor Profile
        </h3>
      </div>
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-shrink-0">
          {isEditing ? (
            <div className="flex flex-col items-center">
              {editableInvestor.imageUrl || editableInvestor.images ? (
                <img
                  src={
                    editableInvestor.imageUrl ||
                    `https://app.bigdogtools.com/sites/image.php?v=2&scale=0.5&dir=vestors&filename=${editableInvestor.images}`
                  }
                  alt="Preview"
                  className="h-32 w-32 rounded-full object-cover bg-white border-4 border-blue-200 dark:border-blue-900 mb-4"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextElementSibling?.style.removeProperty(
                      "display",
                    );
                  }}
                />
              ) : null}
              <div
                className={`h-32 w-32 rounded-full object-cover bg-gray-200 dark:bg-gray-700 border-4 border-blue-200 dark:border-blue-900 mb-4 flex items-center justify-center ${
                  editableInvestor.imageUrl || editableInvestor.images
                    ? "hidden"
                    : ""
                }`}
              >
                <svg
                  className="h-16 w-16 text-gray-400 dark:text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <label className="cursor-pointer px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                Change Image
                <input
                  type="file"
                  name="logo"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>
          ) : editableInvestor.images ? (
            <img
              className="h-32 w-32 rounded-full object-cover border-4 border-blue-200 dark:border-blue-900"
              src={`https://app.bigdogtools.com/sites/image.php?v=2&scale=0.5&dir=vestors&filename=${editableInvestor.images}`}
              alt="Investor Avatar"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextElementSibling?.style.removeProperty("display");
              }}
            />
          ) : null}
          {!isEditing && !editableInvestor.images && (
            <div className="h-32 w-32 rounded-full object-cover bg-gray-200 dark:bg-gray-700 border-4 border-blue-200 dark:border-blue-900 flex items-center justify-center">
              <svg
                className="h-16 w-16 text-gray-400 dark:text-gray-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>
        <div className="flex-1 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name
            </label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                defaultValue={editableInvestor.name}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-lg font-semibold"
              />
            ) : (
              <p className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                {editableInvestor.name}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="phone"
                  defaultValue={editableInvestor.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              ) : (
                <p className="text-gray-700 dark:text-gray-300">
                  {formatMobileNumber(editableInvestor.phone)}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Website
              </label>
              {isEditing ? (
                <input
                  type="text"
                  name="website"
                  defaultValue={editableInvestor.website}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                />
              ) : (
                (() => {
                  const url = editableInvestor.website;
                  const formattedUrl = /^(https?:\/\/)/.test(url)
                    ? url
                    : `https://${url}`;
                  return (
                    <a
                      href={formattedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {editableInvestor.website}
                    </a>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Investment Range Section */}
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
        <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8.16 2.75a.75.75 0 00-1.08.6v5.69H1.75a.75.75 0 00-.75.75v2.5c0 .414.336.75.75.75h5.37v5.69c0 .464.576.734 1.08.6a60.6 60.6 0 018.62-17a.75.75 0 00-.62-1.11H8.16z" />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
          Investment Range
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Minimum Investment
          </label>
          {isEditing ? (
            <input
              type="number"
              name="money_from"
              defaultValue={editableInvestor.money_from}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
              placeholder="Min"
            />
          ) : (
            <p className="text-gray-700 dark:text-gray-300">
              ${editableInvestor.money_from}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Maximum Investment
          </label>
          {isEditing ? (
            <input
              type="number"
              name="money_to"
              defaultValue={editableInvestor.money_to}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
              placeholder="Max"
            />
          ) : (
            <p className="text-gray-700 dark:text-gray-300">
              ${editableInvestor.money_to}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Wants to Lend
          </label>
          {isEditing ? (
            <Select
              value={{
                value: editableInvestor.lend_cash,
                label: editableInvestor.lend_cash === "1" ? "Yes" : "No",
              }}
              onChange={(v) => handleSelectChange("lend_cash", v.value)}
              options={boolOptions}
              classNames={{
                menuButton: ({ isDisabled }) =>
                  `flex text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-emerald-500 focus:ring focus:ring-emerald-500/20 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${
                    isDisabled ? "bg-gray-200" : ""
                  }`,
                menu: "absolute z-10 w-full bg-white shadow-lg border border-gray-300 rounded-md py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200",
                listItem: ({ isSelected }) =>
                  `block transition duration-200 px-3 py-2 cursor-pointer select-none truncate rounded ${
                    isSelected
                      ? "text-white bg-emerald-500"
                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`,
              }}
            />
          ) : (
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                editableInvestor.lend_cash === "1"
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
              }`}
            >
              {editableInvestor.lend_cash === "1" ? "Yes" : "No"}
            </span>
          )}
        </div>
      </div>
    </div>

    {/* Notes Section */}
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
        <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2H4a1 1 0 110-2V4z" />
          </svg>
        </div>
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
          Personal Notes
        </h3>
      </div>
      {isEditing ? (
        <textarea
          name="notes"
          defaultValue={editableInvestor.notes}
          onChange={handleInputChange}
          rows="4"
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
        />
      ) : (
        <p className="text-gray-700 dark:text-gray-300 mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md border border-gray-100 dark:border-gray-600">
          {editableInvestor.notes || "No notes available."}
        </p>
      )}
    </div>
  </div>
);

const AttributesTabContent = ({
  isEditing,
  editableInvestor,
  handleSelectChange,
}) => {
  const counties = (editableInvestor.counties || "")
    .split(", ")
    .map((c) => ({ value: c, label: c }));
  const prop_types = (editableInvestor.prop_types || "")
    .split(", ")
    .map((p) => ({ value: p, label: p }));
  const attributes = (editableInvestor.investment_types || "")
    .split(", ")
    .map((a) => ({ value: a, label: a }));
  const fincance_type = (editableInvestor.fincance_type || "")
    .split(", ")
    .map((f) => ({ value: f, label: f }));

  return (
    <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
      <div>
        <h4 className="text-md font-semibold text-gray-500 uppercase tracking-wider mb-2 dark:text-gray-200">
          Investment County(s)
        </h4>
        {isEditing ? (
          <Select
            isMultiple
            isSearchable
            value={counties}
            onChange={(v) =>
              handleSelectChange("counties", v.map((i) => i.value).join(", "))
            }
            options={countiesOptions}
            classNames={{
              menuButton: ({ isDisabled }) =>
                `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
                  isDisabled ? "bg-gray-200" : ""
                }`,
              menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
              listItem: ({ isSelected }) =>
                `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
                  isSelected
                    ? "text-white bg-indigo-500"
                    : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
                }`,
            }}
          />
        ) : (
          <p className="text-lg text-gray-800 dark:text-gray-200">
            {editableInvestor.counties || "No Area"}
          </p>
        )}
      </div>
      <div>
        <h4 className="text-md font-semibold text-gray-500 uppercase tracking-wider mb-2 dark:text-gray-200">
          Property Types
        </h4>
        {isEditing ? (
          <Select
            isMultiple
            isSearchable
            value={prop_types}
            onChange={(v) =>
              handleSelectChange("prop_types", v.map((i) => i.value).join(", "))
            }
            options={propOption}
            classNames={{
              menuButton: ({ isDisabled }) =>
                `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
                  isDisabled ? "bg-gray-200" : ""
                }`,
              menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
              listItem: ({ isSelected }) =>
                `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
                  isSelected
                    ? "text-white bg-indigo-500"
                    : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
                }`,
            }}
          />
        ) : (
          <p className="text-lg text-gray-800 dark:text-gray-200">
            {editableInvestor.prop_types || "None"}
          </p>
        )}
      </div>
      <div>
        <h4 className="text-md font-semibold text-gray-500 uppercase tracking-wider mb-2 dark:text-gray-200">
          Investment Attributes
        </h4>
        {isEditing ? (
          <Select
            isMultiple
            isSearchable
            value={attributes}
            onChange={(v) =>
              handleSelectChange(
                "investment_types",
                v.map((i) => i.value).join(", "),
              )
            }
            options={attributeOptions}
            classNames={{
              menuButton: ({ isDisabled }) =>
                `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
                  isDisabled ? "bg-gray-200" : ""
                }`,
              menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
              listItem: ({ isSelected }) =>
                `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
                  isSelected
                    ? "text-white bg-indigo-500"
                    : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
                }`,
            }}
          />
        ) : (
          <p className="text-lg text-gray-800 dark:text-gray-200">
            {editableInvestor.investment_types || "N/A"}
          </p>
        )}
      </div>
      <div>
        <h4 className="text-md font-semibold text-gray-500 uppercase tracking-wider mb-2 dark:text-gray-200">
          Financials
        </h4>
        {isEditing ? (
          <Select
            isMultiple
            isSearchable
            value={fincance_type}
            onChange={(v) =>
              handleSelectChange(
                "fincance_type",
                v.map((i) => i.value).join(", "),
              )
            }
            options={financeOptions}
            classNames={{
              menuButton: ({ isDisabled }) =>
                `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
                  isDisabled ? "bg-gray-200" : ""
                }`,
              menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
              listItem: ({ isSelected }) =>
                `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
                  isSelected
                    ? "text-white bg-indigo-500"
                    : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
                }`,
            }}
          />
        ) : (
          <p className="text-lg text-gray-800 dark:text-gray-200">
            {editableInvestor.fincance_type || "N/A"}
          </p>
        )}
      </div>
    </div>
  );
};

// --- Main Investor Page Component ---
const InvestorPages = ({ user }) => {
  const [investorLeads, setInvestorLeads] = useAdaptiveMMKV(
    "user.Feed.Investor.Leads",
    storage,
    [],
  );
  // MUST BE HERE: Inside the component, directly after the state it tracks.
  const leadsTruthRef = useRef(investorLeads);
  useEffect(() => {
    leadsTruthRef.current = investorLeads;
  }, [investorLeads]);

  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [error, setError] = useState(null);
  const [investorAR] = useAdaptiveMMKV(
    "user.Feed.Investor.Responders",
    storage,
  );

  // NEW: State for multi-selection
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const pullLeads = useCallback(async () => {
    try {
      const response = await instance.get(
        `/LoadHomes.php?u=${user?.app_id}&type=investors&_t=${Date.now()}`,
      );
      setInvestorLeads(response.data);
    } catch (e) {
      if (e.response) {
        setError("Error fetching investor data: " + e.response.data[0].error);
        console.error(
          "Error fetching investor data: ",
          e.response.data[0].error,
        );
      } else if (e.request) {
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setError(null);
    }
  }, [user.app_id, setInvestorLeads]);

  const pullResponderData = useCallback(async () => {
    const formData = new FormData();
    formData.append("owner", user.app_id);
    try {
      const response = await instance({
        method: "POST",
        data: formData,
        url: "/investorResponders.php",
        headers: {
          Accept: "application/json",
          "content-Type": "multipart/form-data",
        },
      });
      const myData = [...response.data].sort((a, b) =>
        String(a.short_name).localeCompare(String(b.short_name)),
      );
      storage.set("user.Feed.Investor.Responders.All", JSON.stringify(myData));
    } catch (e) {
      console.error("Error fetching autoresponder data:", e);
    }
  }, [user.app_id]);

  const pullLeadsResponders = useCallback(async () => {
    try {
      const response = await instance({
        method: "GET",
        url:
          "/LoadHomes.php?u=" +
          user.app_id +
          "&type=autoresponderInvestorDISTINCT",
        headers: {
          Accept: "application/json",
          "content-Type": "multipart/form-data",
        },
      });
      storage.set(
        "user.Feed.Investor.Responders",
        JSON.stringify(response.data),
      );
    } catch (e) {
      console.error("Error fetching autoresponder data:", e);
    } finally {
    }
  }, [user.app_id]);

  useEffect(() => {
    pullLeads();
    pullLeadsResponders();
    pullResponderData();
  }, [pullLeads, pullLeadsResponders, pullResponderData]);

  // NEW: Handle select all checkbox (Current Page)
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentIds = currentLeads.map((l) => l.pid);
      setSelectedLeads((prev) => [...new Set([...prev, ...currentIds])]);
    } else {
      const currentIds = currentLeads.map((l) => l.pid);
      setSelectedLeads((prev) => prev.filter((id) => !currentIds.includes(id)));
    }
  };

  // NEW: Handle single row checkbox
  const handleSelectRow = (id) => {
    setSelectedLeads((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const executeBulkDelete = async () => {
    setBulkLoading(true);
    try {
      const formData = new FormData();
      formData.append("ids", selectedLeads.join(","));
      formData.append("owner", user.app_id);
      formData.append("type", "investor_leads");
      formData.append(
        "token",
        token(user.app_id, user.app_email, user.app_api_key),
      );

      await instance.post("/bulkDelete.php", formData);

      // ADD THIS: Optimistic UI Update
      const currentTruth = leadsTruthRef.current || [];
      setInvestorLeads(
        currentTruth.filter((l) => !selectedLeads.includes(l.pid)),
      );

      setError(null);
    } catch (e) {
      console.error("Bulk delete error:", e);
      setError("An error occurred while deleting selected investors.");
    } finally {
      setBulkLoading(false);
      setIsBulkDeleteModalOpen(false);
      await pullLeads();
      setSelectedLeads([]);
    }
  };

  const handleDelete = async (pid) => {
    const data = new FormData();
    data.append("id", pid);
    data.append("owner", user.app_id);
    data.append("token", token(user.app_id, user.app_email, user.app_api_key));

    try {
      await instance.post("/removeInvestor.php", data);

      // ADD THIS: Optimistic UI Update
      const currentTruth = leadsTruthRef.current || [];
      setInvestorLeads(currentTruth.filter((l) => l.pid !== pid));

      setError(null);
    } catch (e) {
      if (e.response) {
        setError("Error removing investor: " + e.response.data[0].message);
        console.error("Error removing investor: ", e.response.data[0].message);
      } else if (e.request) {
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      await pullLeads();
    }
  };

  const filteredLeads =
    investorLeads?.filter((lead) =>
      Object.values(lead).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    ) || [];

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstItem, indexOfLastItem);
  const menuRef = useRef([]);

  const [autoresponderSubMenuOpenForUser, setAutoresponderSubMenuOpenForUser] =
    useState(null);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutside = menuRef.current.every(
        (ref) => ref && !ref.contains(event.target),
      );
      if (isOutside) {
        setMenuOpenFor(null);
        setAutoresponderSubMenuOpenForUser(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSetAutoresponder = async (leadId, sequenceId) => {
    setAutoresponderSubMenuOpenForUser(null);
    setMenuOpenFor(null);

    const data = {
      owner: user.app_id,
      sequence: sequenceId,
      pid: leadId,
    };
    try {
      await instance({
        method: "POST",
        url: "/setAutoResponderInvestor.php",
        data: data,
        headers: {
          Accept: "application/json",
          "content-Type": "multipart/form-data",
        },
      });
    } catch (e) {
      console.error("Error setting autoresponder:", e);
    } finally {
      pullLeads();
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 10;
    const halfMax = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(1, currentPage - halfMax);
    let endPage = Math.min(totalPages, currentPage + halfMax);

    if (currentPage <= halfMax) {
      endPage = Math.min(totalPages, maxPageButtons);
    }
    if (currentPage + halfMax > totalPages) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(
        <span key="ellipsis-start" className="px-2">
          ...
        </span>,
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`
            w-8 h-8 rounded-full text-sm font-medium mx-1 transition-colors duration-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700
            ${
              currentPage === i
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }
          `}
        >
          {i}
        </button>,
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis-end" className="px-2">
          ...
        </span>,
      );
    }

    return pageNumbers;
  };

  const getResponderShortName = useCallback(
    (targetSequence) => {
      const foundResponder = investorAR?.find(
        (responder) => responder.sequence == targetSequence,
      );

      return foundResponder ? foundResponder.short_name : null;
    },
    [investorAR],
  );

  const handleSaveChanges = async (updatedInvestor) => {
    const formData = new FormData();
    formData.append("type", "edit");
    formData.append("owner", user.app_id);

    Object.keys(updatedInvestor).forEach((key) => {
      if (key === "logo") {
        formData.append("logo", updatedInvestor.imageData);
        formData.append("imageName", updatedInvestor.imageName);
      }
      formData.append(key, updatedInvestor[key]);
    });

    try {
      await instance.post("/investorUpdate.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        setError("Error updating investor: " + e.response.data[0].message);
        console.error("Error updating investor: ", e.response[0].data.message);
      } else if (e.request) {
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setIsViewModalOpen(false);
      await pullLeads();
    }
  };

  const openViewModal = (investor) => {
    setSelectedInvestor(investor);
    setIsViewModalOpen(true);
  };

  // NEW: Footer for Bulk Delete Modal
  const bulkDeleteFooter = (
    <div className="flex justify-end gap-2">
      <button
        onClick={() => setIsBulkDeleteModalOpen(false)}
        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
      >
        Cancel
      </button>
      <button
        onClick={executeBulkDelete}
        disabled={bulkLoading}
        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
      >
        {bulkLoading ? "Deleting..." : "Confirm Delete"}
      </button>
    </div>
  );

  return (
    <PageContent title="Investor Leads" subtitle="All Leads" error={error}>
      <div className="p-8 space-y-8">
        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.125-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.125-1.283.356-1.857m0 0a3.001 3.001 0 015.288 0M12 14a4 4 0 100-8 4 4 0 000 8z"
                  />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Investor Network</h2>
                <p className="text-emerald-100">
                  Manage and track all investor opportunities
                </p>
              </div>
            </div>
            <div className="text-right text-emerald-100 text-sm">
              <div className="font-semibold">
                {investorLeads?.length || 0} Total Leads
              </div>
              <div>
                {investorLeads?.filter(
                  (lead) =>
                    lead.fullName
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    lead.email
                      ?.toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    lead.phone?.includes(searchTerm),
                ).length || 0}{" "}
                Matching Search
              </div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-6 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 dark:bg-gray-700/50 rounded-t-xl border-b border-gray-200 dark:border-gray-700">
            <div className="flex w-full sm:w-3/4 gap-3">
              <div className="relative flex-1">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search investors by name, email, or phone..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="block w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 transition-colors"
                />
              </div>
              {/* Bulk Delete Button */}
              {selectedLeads.length > 0 && (
                <button
                  onClick={() => setIsBulkDeleteModalOpen(true)}
                  className="px-4 py-2 text-sm font-semibold rounded-lg text-white bg-red-600 hover:bg-red-700 dark:hover:bg-red-500 whitespace-nowrap transition-colors flex-shrink-0"
                >
                  Delete ({selectedLeads.length})
                </button>
              )}
            </div>

            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="w-full sm:w-auto px-5 py-2 text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 dark:hover:bg-emerald-500 transition-colors flex-shrink-0 whitespace-nowrap"
            >
              + Create Investor
            </button>
          </div>
          <div className="overflow-y-visible">
            <table className="w-full text-sm text-left">
              <thead className="text-xs font-semibold text-gray-700 uppercase bg-gray-50 dark:text-gray-300 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 tracking-wider">
                <tr>
                  {/* Select All Checkbox */}
                  <th className="px-6 py-4 w-4">
                    <input
                      type="checkbox"
                      className="w-4 h-4 text-emerald-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-emerald-500 dark:focus:ring-emerald-400 cursor-pointer"
                      onChange={handleSelectAll}
                      checked={
                        currentLeads.length > 0 &&
                        currentLeads.every((l) => selectedLeads.includes(l.pid))
                      }
                    />
                  </th>
                  <th className="px-6 py-4 text-gray-600 dark:text-gray-400">
                    Investor
                  </th>
                  <th className="px-6 py-4 text-gray-600 dark:text-gray-400">
                    Contact
                  </th>
                  <th className="px-6 py-4 hidden 2xl:table-cell text-gray-600 dark:text-gray-400">
                    Investment Types
                  </th>
                  <th className="px-6 py-4 hidden 2xl:table-cell text-gray-600 dark:text-gray-400">
                    Range
                  </th>
                  <th className="px-6 py-4 text-center text-gray-600 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentLeads.length > 0 ? (
                  currentLeads.map((lead, index) => (
                    <tr
                      key={lead.pid}
                      className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      {/* Row Checkbox */}
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.pid)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectRow(lead.pid);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td
                        className="px-6 py-4 font-medium"
                        onClick={() => openViewModal(lead)}
                      >
                        {lead.name}
                        <div className="text-gray-500 mt-1">
                          {lead.sequence === "0" ? (
                            <></>
                          ) : lead.sequence === "X" ? (
                            <p
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs bg-blue-300 text-white-800 ml-2"`}
                            >
                              <b>Autoresponder: </b> Auto Generated
                            </p>
                          ) : (
                            <p
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs bg-blue-200 text-white-800 ml-2"`}
                            >
                              <b>Autoresponder: </b>{" "}
                              {getResponderShortName(lead.sequence)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td
                        className="px-6 py-4"
                        onClick={() => openViewModal(lead)}
                      >
                        <div>{formatMobileNumber(lead.phone)}</div>
                        <div>{lead.email}</div>
                        <div>{lead.website}</div>
                      </td>
                      <td
                        className="px-6 py-4 hidden 2xl:table-cell"
                        onClick={() => openViewModal(lead)}
                      >
                        {lead.investment_types}
                      </td>
                      <td
                        className="px-6 py-4 hidden 2xl:table-cell"
                        onClick={() => openViewModal(lead)}
                      >
                        ${parseInt(lead.money_from || 0).toLocaleString()} - $
                        {parseInt(lead.money_to || 0).toLocaleString()}
                      </td>
                      <td
                        className="px-6 py-4 text-center relative"
                        ref={(el) => (menuRef.current[index] = el)}
                      >
                        <button
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpenFor(
                              menuOpenFor === lead.pid ? null : lead.pid,
                            );
                          }}
                        >
                          <svg
                            className="w-5 h-5 text-gray-500 dark:text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                          </svg>
                        </button>
                        {menuOpenFor === lead.pid && (
                          <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-10 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900">
                            <button
                              onClick={() => {
                                setAutoresponderSubMenuOpenForUser(
                                  autoresponderSubMenuOpenForUser === lead.pid
                                    ? null
                                    : lead.pid,
                                );
                              }}
                              className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 relative pl-10 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900"
                            >
                              Set Autoresponder
                              <span className="absolute left-3 top-1/2 -translate-y-1/2">
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 19L8 12l7-7"
                                  ></path>
                                </svg>
                              </span>
                            </button>
                            {autoresponderSubMenuOpenForUser === lead.pid && (
                              <div className="absolute top-0 right-full mt-0 mr-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-20 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900">
                                <button
                                  key={"0"}
                                  onClick={() =>
                                    handleSetAutoresponder(lead.pid, 0)
                                  }
                                  className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 pl-10 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900"
                                >
                                  Remove Autoresponder
                                </button>
                                {investorAR.length > 0 ? (
                                  investorAR.map((ar) => (
                                    <button
                                      key={ar.sequence}
                                      onClick={() =>
                                        handleSetAutoresponder(
                                          lead.pid,
                                          ar.sequence,
                                        )
                                      }
                                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100  pl-10 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900"
                                    >
                                      <div className="flex justify-between items-center w-full">
                                        {lead.sequence == ar.sequence && (
                                          <span className="absolute left-3 ">
                                            <svg
                                              className="w-5 h-5 text-green-500"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              ></path>
                                            </svg>
                                          </span>
                                        )}
                                        <span>{ar.short_name}</span>
                                      </div>
                                    </button>
                                  ))
                                ) : (
                                  <div className="block px-4 py-2 text-sm text-gray-500">
                                    No autoresponders found.
                                  </div>
                                )}
                              </div>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(lead.pid);
                              }}
                              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <svg
                          className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.125-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.125-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 11a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                          No investors found
                        </p>
                        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
                          Create a new investor to get started
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredLeads.length > itemsPerPage && (
            <div className="p-6 flex items-center justify-between gap-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 rounded-b-xl">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                ← Previous
              </button>
              <div className="flex-grow flex justify-center gap-1">
                {renderPageNumbers()}
              </div>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
      <CreateInvestorInfoModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        user={user}
        onCreated={pullLeads}
        setError={setError}
      />
      {selectedInvestor && (
        <InvestorDetailModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          onSave={handleSaveChanges}
          investor={selectedInvestor}
        />
      )}
      {/* NEW: Bulk Delete Confirmation Modal */}
      <Modal
        show={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        title="Confirm Bulk Delete"
        footer={bulkDeleteFooter}
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        }
        iconColor="red"
      >
        <p>
          Are you sure you want to delete <b>{selectedLeads.length}</b> selected
          investors? This action cannot be undone.
        </p>
      </Modal>
    </PageContent>
  );
};

export default InvestorPages;
